// src/components/MobileUploadButton.js

import React, {
  useState,
  useRef,
  useContext,
  useCallback,
  useEffect,
} from "react";
import {
  Tooltip,
  Fab,
  CircularProgress,
  Snackbar,
  makeStyles,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
// import DescriptionIcon from "@material-ui/icons/Description"; // Removed
import { FormContext } from "./FormContext";
import ParseDocumentProgressDialog from "./ParseDocumentProgressDialog";

// Define allowed file types and max size
const ALLOWED_FILE_TYPES = [
  ".pdf",
  ".doc",
  ".docx",
  ".jpg",
  ".jpeg",
  ".png",
  ".heic",
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Cloud Function URL
const CLOUD_FUNCTION_URL =
  "https://us-central1-resumerevivalapp.cloudfunctions.net/prod_parse_uploaded_documents_to_application_builder";

const useStyles = makeStyles((theme) => ({
  fabButton: {
    backgroundColor: "#3f51b5",
    color: theme.palette.common.white,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    "&:hover": {
      backgroundColor: "#303f9f",
    },
    marginBottom: theme.spacing(1),
  },
  fabIcon: {
    marginRight: theme.spacing(1),
  },
  fileInput: {
    display: "none",
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

/**
 * Helper function to update localStorage while preserving specific keys.
 * @param {Object} data - The data to store in localStorage.
 * @param {Array<string>} excludeKeys - Keys to exclude from being overwritten.
 */
const updateLocalStorage = (data, excludeKeys = []) => {
  const keysToUpdate = Object.keys(data).filter(
    (key) => !excludeKeys.includes(key)
  );

  keysToUpdate.forEach((key) => {
    try {
      localStorage.setItem(key, JSON.stringify(data[key]));
    } catch (error) {
      console.error(`Failed to store ${key} in localStorage:`, error);
    }
  });
};

/**
 * A mobile-specific upload button component for use in the ToolsFab menu.
 * This is a simplified version of ParseDocumentButton focused on mobile usage.
 */
const MobileUploadButton = ({ isCompact = false, onClose }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const { populateFormData } = useContext(FormContext);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Progress dialog state
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // handleUploadClick is no longer needed as the Fab will be a label

  /**
   * Validate the selected file type.
   * @param {File} file - The file to validate.
   * @returns {boolean} - Whether the file type is allowed.
   */
  const isValidFileType = (file) => {
    const fileName = file.name.toLowerCase();
    return ALLOWED_FILE_TYPES.some((type) => fileName.endsWith(type));
  };

  /**
   * Handle file selection and initiate the parsing process.
   * @param {Event} event - The file input change event.
   */
  const handleFileChange = async (event) => {
    console.log("MobileUploadButton: handleFileChange triggered.");
    const files = event.target.files;
    if (!files || files.length === 0) {
      console.log(
        "MobileUploadButton: No files selected or event.target.files is null."
      );
      return;
    }
    console.log("MobileUploadButton: Files selected:", files);

    // Reset states before validation
    setUploadProgress(0);
    setUploadSuccess(false);
    setUploadError(null);
    setSelectedFiles(Array.from(files));
    // Note: setLoading(true) and setProgressDialogOpen(true) will be called AFTER validation

    // Validate each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`MobileUploadButton: Validating file ${i + 1}: ${file.name}`);
      if (!isValidFileType(file)) {
        console.error("MobileUploadButton: Invalid file type.", file.name);
        setSnackbar({
          open: true,
          message: `Invalid file type. Please upload one of the following: ${ALLOWED_FILE_TYPES.join(
            ", "
          )}`,
          severity: "error",
        });
        resetFileInput();
        // No need to set loading/dialog false here as they are not true yet
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        console.error(
          "MobileUploadButton: File size exceeds limit.",
          file.name
        );
        setSnackbar({
          open: true,
          message:
            "File size exceeds the 5MB limit. Please upload a smaller file.",
          severity: "error",
        });
        resetFileInput();
        // No need to set loading/dialog false here as they are not true yet
        return;
      }
    }
    console.log("MobileUploadButton: All files validated successfully.");

    // Set loading state and show progress dialog now that validation has passed
    console.log(
      "MobileUploadButton: Setting loading and progressDialogOpen to true post-validation."
    );
    setLoading(true);
    setProgressDialogOpen(true);

    try {
      console.log("MobileUploadButton: Preparing FormData.");
      const formData = new FormData();
      // Append all files
      for (let i = 0; i < files.length; i++) {
        formData.append("file", files[i]);
      }

      // Set initial progress before fetch
      // if (isMountedRef.current) {
      //   console.log("MobileUploadButton: Setting upload progress to 10.");
      //   setUploadProgress(10);
      // }

      // Simulate progress updates
      let progressInterval;
      if (isMountedRef.current) {
        setUploadProgress(10); // Start at 10%
        progressInterval = setInterval(() => {
          if (isMountedRef.current) {
            setUploadProgress((prevProgress) => {
              const newProgress = prevProgress + (100 - prevProgress) * 0.05; // Slower approach to 95
              return Math.min(newProgress, 95); // Cap at 95% until fetch completes
            });
          } else {
            clearInterval(progressInterval);
          }
        }, 500);
      }

      console.log("MobileUploadButton: Initiating fetch to cloud function.");
      const response = await fetch(CLOUD_FUNCTION_URL, {
        method: "POST",
        body: formData,
      });
      console.log("MobileUploadButton: Fetch response received.", response);

      // Clear the progress interval
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      // If component is unmounted, don't proceed with state updates
      if (!isMountedRef.current) {
        console.log(
          "MobileUploadButton: Component unmounted after fetch. Aborting state updates."
        );
        return;
      }

      // Update progress after fetch has initiated (now handled by interval and final set)
      // if (isMountedRef.current) {
      //   console.log("MobileUploadButton: Setting upload progress to 50.");
      //   setUploadProgress(50);
      // }

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: "Failed to parse error response." }));
        const errorMsg =
          errorData.message ||
          `Error parsing document: ${response.statusText} (status: ${response.status})`;
        console.error("MobileUploadButton: Fetch error:", errorMsg);
        throw new Error(errorMsg);
      }

      console.log(
        "MobileUploadButton: Fetch successful. Parsing JSON response."
      );
      const resultData = await response.json();
      console.log("MobileUploadButton: JSON response parsed:", resultData);

      // If component is unmounted, don't proceed with state updates
      if (!isMountedRef.current) {
        console.log(
          "MobileUploadButton: Component unmounted before populating form. Aborting state updates."
        );
        return;
      }

      // Update FormContext
      console.log("MobileUploadButton: Populating form data.");
      populateFormData(resultData);

      // Update localStorage while preserving 'targetJobInformation'
      console.log("MobileUploadButton: Updating localStorage.");
      updateLocalStorage(resultData, ["targetJobInformation"]);

      console.log(
        "MobileUploadButton: Documents parsed successfully:",
        resultData
      );

      // Set progress to 100% and mark as success
      if (isMountedRef.current) {
        console.log(
          "MobileUploadButton: Setting upload progress to 100 and success to true."
        );
        setUploadProgress(100);
        setUploadSuccess(true);

        // Show success snackbar
        console.log("MobileUploadButton: Displaying success snackbar.");
        setSnackbar({
          open: true,
          message: "Documents parsed and form autofilled successfully!",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("MobileUploadButton: Error in try block:", error);

      // If component is unmounted, don't proceed with state updates
      if (!isMountedRef.current) {
        console.log(
          "MobileUploadButton: Component unmounted during error handling. Aborting state updates."
        );
        return;
      }

      // Set error state
      console.log("MobileUploadButton: Setting error state.");
      setUploadError(
        error.message ||
          "There was an error uploading or parsing your documents. Please try again."
      );

      // Show error snackbar
      console.log("MobileUploadButton: Displaying error snackbar.");
      setSnackbar({
        open: true,
        message:
          error.message ||
          "There was an error uploading or parsing your documents. Please try again.",
        severity: "error",
      });
    } finally {
      console.log("MobileUploadButton: Entering finally block.");
      if (isMountedRef.current) {
        console.log(
          "MobileUploadButton: Setting loading to false and resetting file input."
        );
        setLoading(false);
        resetFileInput();
      } else {
        console.log(
          "MobileUploadButton: Component unmounted in finally block."
        );
      }
    }
  };

  /**
   * Handle closing the progress dialog
   */
  const handleCloseProgressDialog = () => {
    // Only allow closing if upload is complete or there was an error
    if (uploadSuccess || uploadError) {
      setProgressDialogOpen(false);
      // Reset states
      setUploadProgress(0);
      setUploadSuccess(false);
      setUploadError(null);
      setSelectedFiles([]);
    } else if (window.confirm("Are you sure you want to cancel the upload?")) {
      setProgressDialogOpen(false);
      // Reset states
      setUploadProgress(0);
      setUploadSuccess(false);
      setUploadError(null);
      setSelectedFiles([]);
      setLoading(false);
    }
  };

  /**
   * Reset the file input value to allow re-uploading the same file if needed.
   */
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Handle closing of the Snackbar.
   */
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_FILE_TYPES.join(", ")}
        multiple
        className={classes.fileInput}
        onChange={handleFileChange}
        aria-label="Upload documents"
        id="mobile-file-input"
      />

      <Tooltip
        title={
          loading
            ? "Processing documents..."
            : "Upload documents to autofill your application"
        }
        arrow
        placement="left"
      >
        <div>
          <Fab
            component="label"
            htmlFor="mobile-file-input"
            size="medium"
            aria-label={loading ? "Processing documents" : "Upload documents"}
            className={classes.fabButton}
            // onClick={handleUploadClick} // Removed: label behavior handles this
            disabled={loading}
            style={{
              position: "relative",
              backgroundColor: loading ? "#9fa8da" : "#3f51b5",
            }}
          >
            {loading ? (
              <>
                <CloudUploadIcon style={{ opacity: 0.5 }} />
                <CircularProgress
                  size={36}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: -18,
                    marginLeft: -18,
                  }}
                />
              </>
            ) : isCompact ? (
              <img
                src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/parsing.svg?alt=media&token=75b51beb-a4f6-40f6-a986-34358fd6ed82"
                alt="Parse icon"
                style={{
                  width: 24,
                  height: 24,
                  filter: "brightness(0) invert(1)",
                }}
              />
            ) : (
              <CloudUploadIcon />
            )}
          </Fab>
        </div>
      </Tooltip>

      {/* Progress Dialog */}
      <ParseDocumentProgressDialog
        open={progressDialogOpen}
        onClose={handleCloseProgressDialog}
        files={selectedFiles}
        progress={uploadProgress}
        success={uploadSuccess}
        error={uploadError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MobileUploadButton;
